import { ApolloError } from "@apollo/client";

export const OTP_RESEND_SECONDS = 60;

export const ProfileErrorCode = {
  MaxProfiles: "MAX_PROFILES_PER_USER_EXCEEDED",
  AccountPinRequired: "MAIN_ACCOUNT_PIN_SETUP_REQUIRED",
  IllegalWords: "ILLEGAL_WORDS",
  UsernameExists: "USERNAME_ALREADY_EXISTS",
  MissingPin: "MISSING_PIN",
  InvalidPin: "INVALID_PIN",
  OtpCooldown: "OTP_SEND_COOLDOWN",
  OtpSendFailed: "OTP_SEND_FAILED",
  OtpLockdown: "OTP_LOCKDOWN",
  InvalidOtp: "INVALID_OTP",
  OtpExpired: "OTP_EXPIRED",
  InvalidVerificationRequest: "INVALID_VERIFICATION_REQUEST",
  ProfileTokenInvalid: "PROFILE_TOKEN_INVALID",
  ProofAccountPinRequired: "ACCOUNT_PIN_REQUIRED",
  ProofPinRequired: "PIN_REQUIRED",
  ProofForbidden: "FORBIDDEN",
} as const;

// The PIN verification token was missing, expired or not strong enough, so the
// user has to enter a PIN again rather than read an error.
const PROOF_REJECTED_CODES: string[] = [
  ProfileErrorCode.ProfileTokenInvalid,
  ProfileErrorCode.ProofAccountPinRequired,
  ProfileErrorCode.ProofPinRequired,
  ProfileErrorCode.ProofForbidden,
];

export const GENERIC_PROFILE_ERROR_KEY =
  "ProfileError_Something went wrong, please try again.";

const MESSAGE_KEYS: Record<string, string> = {
  [ProfileErrorCode.MaxProfiles]:
    "ProfileError_You have reached the maximum of 5 profiles.",
  [ProfileErrorCode.AccountPinRequired]:
    "ProfileError_Create an Account PIN first.",
  [ProfileErrorCode.IllegalWords]: "ProfileError_This name is not allowed.",
  [ProfileErrorCode.UsernameExists]:
    "ProfileError_Username already exists. Please choose a different username.",
  [ProfileErrorCode.MissingPin]: "ProfileError_This profile needs its PIN.",
  [ProfileErrorCode.InvalidPin]:
    "ProfileError_The PIN is not correct, please try again.",
  [ProfileErrorCode.OtpCooldown]:
    "ProfileError_Please wait 30 seconds before requesting a new code.",
  [ProfileErrorCode.OtpSendFailed]:
    "ProfileError_We could not send the code. Please try again.",
  [ProfileErrorCode.OtpLockdown]:
    "ProfileError_Too many attempts. Try again in 5 minutes.",
  [ProfileErrorCode.InvalidOtp]:
    "ProfileError_The code is not correct, please enter the correct code",
  [ProfileErrorCode.OtpExpired]:
    "ProfileError_This code has expired. Request a new one.",
  [ProfileErrorCode.InvalidVerificationRequest]:
    "ProfileError_This verification is no longer valid. Please start again.",
};

export const getProfileErrorCode = (error: unknown): string =>
  String((error as ApolloError)?.graphQLErrors?.[0]?.extensions?.code ?? "");

export const isPinProofRejected = (error: unknown) =>
  PROOF_REJECTED_CODES.includes(getProfileErrorCode(error));

export const getProfileErrorKey = (error: unknown): string =>
  MESSAGE_KEYS[getProfileErrorCode(error)] ?? GENERIC_PROFILE_ERROR_KEY;
